import {
  TOGGLE_CHAT_AGENT,
  SEND_MESSAGE_REQUEST,
  SEND_MESSAGE_SUCCESS,
  SEND_MESSAGE_FAILURE,
  SEND_MESSAGE_CANCEL,
  SET_CHAT_CONTEXT,
  ADD_USER_MESSAGE,
  MESSAGE_TYPES,
  // Conversation management constants
  CREATE_CONVERSATION_REQUEST,
  CREATE_CONVERSATION_SUCCESS,
  CREATE_CONVERSATION_FAILURE,
  LOAD_CONVERSATIONS_REQUEST,
  LOAD_CONVERSATIONS_SUCCESS,
  LOAD_CONVERSATIONS_FAILURE,
  SWITCH_CONVERSATION,
  LOAD_CONVERSATION_DETAILS_REQUEST,
  LOAD_CONVERSATION_DETAILS_SUCCESS,
  LOAD_CONVERSATION_DETAILS_FAILURE,
  DELETE_CONVERSATION_REQUEST,
  DELETE_CONVERSATION_SUCCESS,
  DELETE_CONVERSATION_FAILURE,
  UPDATE_CONVERSATION_TITLE_REQUEST,
  UPDATE_CONVERSATION_TITLE_SUCCESS,
  UPDATE_CONVERSATION_TITLE_FAILURE,
  SET_ACTIVE_CONVERSATION,
  SET_LAST_EMPTY_CONVERSATION,
  // Progress tracking constants
  PROGRESS_UPDATE,
  AGENT_MESSAGE_UPDATE,
  PIPELINE_START,
  PIPELINE_COMPLETE,
  PIPELINE_ERROR,
  RESET_PROGRESS,
  TITLE_UPDATED,
  THINKING_STREAM,
  // Category disambiguation
  CATEGORY_SELECTION_SUBMITTED,
  STORE_CATEGORY_SELECTION_REQUEST,
  STORE_CATEGORY_SELECTION_SUCCESS,
  STORE_CATEGORY_SELECTION_FAILURE,
  // Message feedback
  UPDATE_MESSAGE_FEEDBACK,
  // Message regeneration
  REGENERATE_MESSAGE_REQUEST,
  REGENERATE_MESSAGE_SUCCESS,
  REGENERATE_MESSAGE_FAILURE,
  REMOVE_AGENT_MESSAGE,
  // Screen and filter management
  SET_CURRENT_SCREEN,
  FETCH_CHAT_FILTERS_REQUEST,
  FETCH_CHAT_FILTERS_SUCCESS,
  FETCH_CHAT_FILTERS_FAILURE,
} from "./constants";

const initialState = {
  isOpen: false,
  isLoading: false,
  error: null,
  context: null,
  
  // Screen management - tracks which screen chat is active on
  currentScreen: null, // 'brandPerformance' | 'marketShare' | null
  
  // Independent filter definitions for chat templates
  filterDefinitions: [],
  dynamicBrands: [],    // Brands fetched for selected retailer
  dynamicKeywords: [],  // Keywords fetched for selected retailer
  isLoadingFilters: false,
  filterError: null,
  
  // Conversation-based state
  activeConversationId: null,
  lastEmptyConversationId: null,
  
  // All conversations data (keyed by conversation_id)
  conversations: {},
  
  // Conversations list (metadata only)
  conversationsList: [],
  isLoadingConversations: false,
  conversationsError: null,
  
  // UI states
  isCreatingConversation: false,
  createConversationError: null,
  isDeletingConversation: false,
  deleteConversationError: null,
  isLoadingConversationDetails: false,
  loadConversationDetailsError: null,
  
  // Progress tracking state
  pipelineActive: false,
  pipelineComplete: false,
  pipelineError: null,
  stages: [],
  currentStage: null,
  agentMessages: [],
  executionPath: null,
  thinkingStreams: {}, // { [stageId]: "accumulated text..." }
  
  // Backward compatibility fields
  currentSessionId: null,
  messages: [],
};

export const chatAgentReducer = (state = initialState, action) => {
  switch (action.type) {
    // ========================================
    // SCREEN AND FILTER MANAGEMENT
    // ========================================
    case SET_CURRENT_SCREEN:
      return {
        ...state,
        currentScreen: action.payload.screen,
      };

    case FETCH_CHAT_FILTERS_REQUEST:
      return {
        ...state,
        isLoadingFilters: true,
        filterError: null,
      };

    case FETCH_CHAT_FILTERS_SUCCESS:
      return {
        ...state,
        isLoadingFilters: false,
        // Update filterDefinitions if provided (from getFilterDefinitions)
        filterDefinitions: action.payload.filterDefinitions !== undefined 
          ? action.payload.filterDefinitions 
          : state.filterDefinitions,
        // Update dynamic brands if provided (from getDynamicFilterValues)
        dynamicBrands: action.payload.dynamicBrands !== undefined 
          ? action.payload.dynamicBrands 
          : state.dynamicBrands,
        // Update dynamic keywords if provided (from getDynamicFilterValues)
        dynamicKeywords: action.payload.dynamicKeywords !== undefined 
          ? action.payload.dynamicKeywords 
          : state.dynamicKeywords,
        filterError: null,
      };

    case FETCH_CHAT_FILTERS_FAILURE:
      return {
        ...state,
        isLoadingFilters: false,
        filterError: action.payload.error || action.payload,
      };

    // ========================================
    // TOGGLE AND CONTEXT
    // ========================================
    case TOGGLE_CHAT_AGENT:
      return {
        ...state,
        isOpen: !state.isOpen,
        error: null,
      };

    case SET_CHAT_CONTEXT:
      return {
        ...state,
        context: action.payload.context,
      };

    case SET_LAST_EMPTY_CONVERSATION:
      return {
        ...state,
        lastEmptyConversationId: action.payload.conversationId,
      };

    // ========================================
    // USER MESSAGE
    // ========================================
    case ADD_USER_MESSAGE: {
      const { conversationId: userConvId, message: userMessage } = action.payload;
      const activeConvId = userConvId || state.activeConversationId;
      
      if (!activeConvId) return state;
      
      const updatedConversations = {
        ...state.conversations,
        [activeConvId]: {
          ...state.conversations[activeConvId],
          messages: [
            ...(state.conversations[activeConvId]?.messages || []),
            userMessage
          ]
        }
      };
      
      // Update conversationsList with preview from user message (for immediate sidebar update)
      const existingConvInList = state.conversationsList.find(c => c.conversation_id === activeConvId);
      const userMessageText = userMessage.text || userMessage.content;
      const existingMessages = updatedConversations[activeConvId]?.messages || [];
      const isFirstMessage = existingMessages.length === 1;
      
      let updatedConversationsList = state.conversationsList;
      if (existingConvInList && isFirstMessage && userMessageText) {
        // Update preview for first message to show in sidebar immediately
        updatedConversationsList = state.conversationsList.map(conv =>
          conv.conversation_id === activeConvId
            ? { 
                ...conv, 
                preview: conv.preview || userMessageText,
                message_count: existingMessages.length,
                updated_at: new Date().toISOString()
              }
            : conv
        );
      } else if (!existingConvInList) {
        // Add new conversation to the list
        updatedConversationsList = [
          {
            conversation_id: activeConvId,
            title: null,
            preview: userMessageText || null,
            message_count: existingMessages.length,
            created_at: state.conversations[activeConvId]?.createdAt || new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          ...state.conversationsList
        ];
      }
      
      return {
        ...state,
        conversations: updatedConversations,
        conversationsList: updatedConversationsList,
        messages: updatedConversations[state.activeConversationId]?.messages || [],
      };
    }

    // ========================================
    // SEND MESSAGE
    // ========================================
    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case SEND_MESSAGE_SUCCESS: {
      const { response, conversationId: responseConvId } = action.payload;
      const targetConvId = responseConvId || state.activeConversationId;
      
      if (!targetConvId) {
        return { ...state, isLoading: false };
      }
      
        const isCategorySelection = response?.metadata?.type === 'category_selection';
        const messageToStore = isCategorySelection
          ? {
              id: response.id || Date.now() + 1,
              type: 'CATEGORY_SELECTION',
              sender: 'agent',
              timestamp: new Date().toISOString(),
              conversationId: targetConvId,
              selectionPayload: response.metadata.selection_payload,
              submitted: false,
              selectedValues: [],
            }
          : response;

      const updatedConversationsSuccess = {
        ...state.conversations,
        [targetConvId]: {
          ...state.conversations[targetConvId],
          messages: [
            ...(state.conversations[targetConvId]?.messages || []),
            messageToStore
          ]
        }
      };
      
      const hasMessages = updatedConversationsSuccess[targetConvId]?.messages?.length > 0;
      
      // Update conversationsList with new title/preview if available
      // This ensures the sidebar re-renders when the first message response arrives
      const existingConvInList = state.conversationsList.find(c => c.conversation_id === targetConvId);
      const newTitle = response.metadata?.conversation_title || response.metadata?.title;
      const existingMessages = updatedConversationsSuccess[targetConvId]?.messages || [];
      const firstUserMessage = existingMessages.find(m => m.type === 'USER' || m.sender === 'user');
      const previewText = firstUserMessage?.text || firstUserMessage?.content;
      
      // Update the conversationsList - always update to ensure preview/title sync
      let updatedConversationsList = state.conversationsList;
      if (existingConvInList) {
        // Always update the conversation in the list to ensure title/preview are synced
        updatedConversationsList = state.conversationsList.map(conv =>
          conv.conversation_id === targetConvId
            ? { 
                ...conv, 
                title: newTitle || conv.title,
                preview: previewText || conv.preview,
                message_count: existingMessages.length,
                updated_at: new Date().toISOString()
              }
            : conv
        );
      } else {
        // Add new conversation to the list (at the beginning since it's the newest)
        updatedConversationsList = [
          {
            conversation_id: targetConvId,
            title: newTitle || null,
            preview: previewText || null,
            message_count: existingMessages.length,
            created_at: state.conversations[targetConvId]?.createdAt || new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          ...state.conversationsList
        ];
      }
      
      return {
        ...state,
        conversations: updatedConversationsSuccess,
        conversationsList: updatedConversationsList,
        messages: updatedConversationsSuccess[state.activeConversationId]?.messages || [],
        lastEmptyConversationId: hasMessages && state.lastEmptyConversationId === targetConvId 
          ? null 
          : state.lastEmptyConversationId,
        isLoading: false,
        error: null,
      };
    }

    case SEND_MESSAGE_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
      };

    case SEND_MESSAGE_CANCEL:
      return {
        ...state,
        isLoading: false,
        error: null,
      };

    // ========================================
    // ACTIVE CONVERSATION
    // ========================================
    case SET_ACTIVE_CONVERSATION:
      return {
        ...state,
        activeConversationId: action.payload.conversationId,
        currentSessionId: action.payload.conversationId,
        messages: state.conversations[action.payload.conversationId]?.messages || [],
      };

    // ========================================
    // CREATE CONVERSATION
    // ========================================
    case CREATE_CONVERSATION_REQUEST:
      return {
        ...state,
        isCreatingConversation: true,
        createConversationError: null,
      };

    case CREATE_CONVERSATION_SUCCESS: {
      const newConvId = action.payload.conversationId;
      const createdAt = action.payload.createdAt || new Date().toISOString();
      
      // Add new conversation to conversationsList at the beginning
      const newConversationForList = {
        conversation_id: newConvId,
        title: null,
        preview: null,
        message_count: 0,
        created_at: createdAt,
        updated_at: createdAt
      };
      
      return {
        ...state,
        isCreatingConversation: false,
        createConversationError: null,
        activeConversationId: newConvId,
        currentSessionId: newConvId,
        lastEmptyConversationId: newConvId,
        conversations: {
          ...state.conversations,
          [newConvId]: {
            messages: [],
            title: null,
            createdAt: createdAt,
            updatedAt: createdAt,
            hasAnalysisData: false,
            brands: [],
            pipeline: {
              pipelineActive: false,
              pipelineComplete: false,
              pipelineError: null,
              stages: [],
              currentStage: null,
              agentMessages: [],
              executionPath: null,
            }
          }
        },
        conversationsList: [newConversationForList, ...state.conversationsList],
        messages: [],
      };
    }

    case CREATE_CONVERSATION_FAILURE:
      return {
        ...state,
        isCreatingConversation: false,
        createConversationError: action.payload.error,
      };

    // ========================================
    // LOAD CONVERSATIONS LIST
    // ========================================
    case LOAD_CONVERSATIONS_REQUEST:
      return {
        ...state,
        isLoadingConversations: true,
        conversationsError: null,
      };

    case LOAD_CONVERSATIONS_SUCCESS:
      return {
        ...state,
        isLoadingConversations: false,
        conversationsError: null,
        conversationsList: action.payload.conversations,
      };

    case LOAD_CONVERSATIONS_FAILURE:
      return {
        ...state,
        isLoadingConversations: false,
        conversationsError: action.payload.error,
      };

    // ========================================
    // LOAD CONVERSATION DETAILS
    // ========================================
    case LOAD_CONVERSATION_DETAILS_REQUEST:
      return {
        ...state,
        isLoadingConversationDetails: true,
        loadConversationDetailsError: null,
      };

    case LOAD_CONVERSATION_DETAILS_SUCCESS: {
      const { conversationId: loadedConvId, conversation } = action.payload;
      const existingConv = state.conversations[loadedConvId];
      const existingPipeline = existingConv?.pipeline;
      
      // Process messages to detect category selection messages that may have lost their type
      const processedMessages = (conversation.messages || []).map(message => {
        // Restore CATEGORY_SELECTION type for picker messages
        if (message.selectionPayload && !message.type) {
          return {
            ...message,
            type: 'CATEGORY_SELECTION',
            submitted: message.submitted || false,
            selectedValues: message.selectedValues || [],
          };
        }
        // Handle legacy messages saved as AGENT with __CATEGORY_SELECTION__ content
        if (message.type === 'AGENT' && message.content === '__CATEGORY_SELECTION__') {
          return null;
        }
        return message;
      }).filter(Boolean);
      
      return {
        ...state,
        isLoadingConversationDetails: false,
        loadConversationDetailsError: null,
        conversations: {
          ...state.conversations,
          [loadedConvId]: {
            messages: processedMessages,
            title: conversation.title,
            createdAt: conversation.created_at,
            updatedAt: conversation.updated_at,
            hasAnalysisData: conversation.has_analysis_data,
            brands: conversation.brands || [],
            pipeline: existingPipeline || {
              pipelineActive: false,
              pipelineComplete: false,
              pipelineError: null,
              stages: [],
              currentStage: null,
              agentMessages: [],
              executionPath: null,
            }
          }
        },
        messages: state.activeConversationId === loadedConvId 
          ? processedMessages
          : state.messages,
      };
    }

    case LOAD_CONVERSATION_DETAILS_FAILURE:
      return {
        ...state,
        isLoadingConversationDetails: false,
        loadConversationDetailsError: action.payload.error,
      };

    // ========================================
    // DELETE CONVERSATION
    // ========================================
    case DELETE_CONVERSATION_REQUEST:
      return {
        ...state,
        isDeletingConversation: true,
        deleteConversationError: null,
      };

    case DELETE_CONVERSATION_SUCCESS: {
      const deletedConvId = action.payload.conversationId;
      const { [deletedConvId]: deleted, ...remainingConversations } = state.conversations;
      
      return {
        ...state,
        isDeletingConversation: false,
        deleteConversationError: null,
        conversations: remainingConversations,
        activeConversationId: state.activeConversationId === deletedConvId ? null : state.activeConversationId,
        currentSessionId: state.activeConversationId === deletedConvId ? null : state.currentSessionId,
        messages: state.activeConversationId === deletedConvId ? [] : state.messages,
        lastEmptyConversationId: state.lastEmptyConversationId === deletedConvId ? null : state.lastEmptyConversationId,
      };
    }

    case DELETE_CONVERSATION_FAILURE:
      return {
        ...state,
        isDeletingConversation: false,
        deleteConversationError: action.payload.error,
      };

    // ========================================
    // UPDATE CONVERSATION TITLE
    // ========================================
    case UPDATE_CONVERSATION_TITLE_REQUEST:
      return {
        ...state,
        isUpdatingTitle: true,
        updateTitleError: null,
      };

    case UPDATE_CONVERSATION_TITLE_SUCCESS: {
      const { conversationId: titleConvId, title: newTitle } = action.payload;
      return {
        ...state,
        isUpdatingTitle: false,
        updateTitleError: null,
        conversations: {
          ...state.conversations,
          [titleConvId]: {
            ...state.conversations[titleConvId],
            title: newTitle
          }
        },
        conversationsList: state.conversationsList.map(conv =>
          conv.conversation_id === titleConvId
            ? { ...conv, title: newTitle }
            : conv
        )
      };
    }

    case UPDATE_CONVERSATION_TITLE_FAILURE:
      return {
        ...state,
        isUpdatingTitle: false,
        updateTitleError: action.payload.error,
      };

    // ========================================
    // TITLE UPDATED (VIA WEBSOCKET)
    // ========================================
    case TITLE_UPDATED: {
      const { conversation_id, title } = action.payload;
      if (!conversation_id || !title) {
        console.warn('⚠️ TITLE_UPDATED: Missing conversation_id or title');
        return state;
      }
      
      return {
        ...state,
        // Update in conversationsList (for sidebar)
        conversationsList: state.conversationsList.map(conv =>
          conv.conversation_id === conversation_id
            ? { ...conv, title, updated_at: new Date().toISOString() }
            : conv
        ),
        // Also update in conversations object
        conversations: {
          ...state.conversations,
          [conversation_id]: {
            ...state.conversations[conversation_id],
            title
          }
        }
      };
    }

    // ========================================
    // PIPELINE START
    // ========================================
    case PIPELINE_START: {
      const startConvId = action.payload?.conversationId || state.activeConversationId;
      if (!startConvId || !state.conversations[startConvId]) return state;
      
      const isActiveConvStart = startConvId === state.activeConversationId;
      
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [startConvId]: {
            ...state.conversations[startConvId],
            pipeline: {
              pipelineActive: true,
              pipelineComplete: false,
              pipelineError: null,
              stages: [],
              currentStage: null,
              agentMessages: [],
              executionPath: null,
              thinkingStreams: {},
            }
          }
        },
        ...(isActiveConvStart ? {
          pipelineActive: true,
          pipelineComplete: false,
          pipelineError: null,
          stages: [],
          currentStage: null,
          agentMessages: [],
          executionPath: null,
          thinkingStreams: {},
        } : {})
      };
    }

    // ========================================
    // PROGRESS UPDATE
    // ========================================
    case PROGRESS_UPDATE: {
      const { 
        stage, status, progress, preview, tool_name, 
        name, icon, description, currentAction,
        conversationId: progressUpdateConvId
      } = action.payload;
      
      const progressTargetConvId = progressUpdateConvId || state.activeConversationId;
      if (!progressTargetConvId || !state.conversations[progressTargetConvId]) return state;
      
      const currentConv = state.conversations[progressTargetConvId];
      const currentPipeline = currentConv.pipeline || {};
      
      let updatedStages = [...(currentPipeline.stages || [])];
      const stageIndex = updatedStages.findIndex(s => s.id === stage);
      
      const stageData = {
        id: stage,
        stage: stage,
        status,
        progress: progress || 0,
        preview: tool_name ? `${preview} [${tool_name}]` : preview,
        error: status === 'error' ? preview : null,
        name: name,
        icon: icon,
        description: description,
        currentAction: currentAction || (status === 'active' && preview ? preview : null)
      };
      
      if (stageIndex >= 0) {
        updatedStages[stageIndex] = { ...updatedStages[stageIndex], ...stageData };
      } else {
        updatedStages.push(stageData);
      }
      
      const newCurrentStage = status === 'active' ? stage : (status === 'completed' ? null : currentPipeline.currentStage);
      const isActiveConvProgress = progressTargetConvId === state.activeConversationId;
      
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [progressTargetConvId]: {
            ...currentConv,
            pipeline: {
              ...currentPipeline,
              stages: updatedStages,
              currentStage: newCurrentStage
            }
          }
        },
        ...(isActiveConvProgress ? {
          stages: updatedStages,
          currentStage: newCurrentStage
        } : {})
      };
    }

    // ========================================
    // AGENT MESSAGE UPDATE
    // ========================================
    case AGENT_MESSAGE_UPDATE: {
      const agentMsgConvId = action.payload.conversationId || state.activeConversationId;
      if (!agentMsgConvId || !state.conversations[agentMsgConvId]) return state;
      
      const agentMsgConv = state.conversations[agentMsgConvId];
      const agentMsgPipeline = agentMsgConv.pipeline || {};
      
      const newAgentMessages = [...(agentMsgPipeline.agentMessages || []).slice(-19), {
        agent: action.payload.agent,
        message: action.payload.message,
        messageType: action.payload.message_type,
        timestamp: action.payload.timestamp
      }];
      
      const isActiveConvAgentMsg = agentMsgConvId === state.activeConversationId;
      
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [agentMsgConvId]: {
            ...agentMsgConv,
            pipeline: {
              ...agentMsgPipeline,
              agentMessages: newAgentMessages
            }
          }
        },
        ...(isActiveConvAgentMsg ? {
          agentMessages: newAgentMessages
        } : {})
      };
    }

    // ========================================
    // THINKING STREAM
    // ========================================
    case THINKING_STREAM: {
      const { stage: streamStage, text: streamText, conversationId: streamConvId } = action.payload;
      const targetConvId = streamConvId || state.activeConversationId;
      if (!targetConvId || !state.conversations[targetConvId]) return state;

      const streamConv = state.conversations[targetConvId];
      const streamPipeline = streamConv.pipeline || {};
      const prevStreams = streamPipeline.thinkingStreams || {};

      const updatedThinkingStreams = {
        ...prevStreams,
        [streamStage]: (prevStreams[streamStage] || '') + streamText,
      };

      const isActiveConvStream = targetConvId === state.activeConversationId;

      return {
        ...state,
        conversations: {
          ...state.conversations,
          [targetConvId]: {
            ...streamConv,
            pipeline: {
              ...streamPipeline,
              thinkingStreams: updatedThinkingStreams,
            },
          },
        },
        ...(isActiveConvStream ? { thinkingStreams: updatedThinkingStreams } : {}),
      };
    }

    // ========================================
    // PIPELINE COMPLETE
    // ========================================
    case PIPELINE_COMPLETE: {
      const completeConvId = action.payload?.conversationId || state.activeConversationId;
      
      if (!completeConvId || !state.conversations[completeConvId]) {
        return state;
      }
      
      const completeConv = state.conversations[completeConvId];
      const completePipeline = completeConv.pipeline || {};
      const isActiveConvComplete = completeConvId === state.activeConversationId;
      
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [completeConvId]: {
            ...completeConv,
            pipeline: {
              ...completePipeline,
              pipelineActive: false,
              pipelineComplete: true,
              currentStage: null,
              executionPath: action.payload?.execution_path || null
            }
          }
        },
        ...(isActiveConvComplete ? {
          pipelineActive: false,
          pipelineComplete: true,
          currentStage: null,
          executionPath: action.payload?.execution_path || null,
          isLoading: false
        } : {})
      };
    }

    // ========================================
    // PIPELINE ERROR
    // ========================================
    case PIPELINE_ERROR: {
      const errorConvId = action.payload?.conversationId || state.activeConversationId;
      if (!errorConvId || !state.conversations[errorConvId]) return state;
      
      const errorConv = state.conversations[errorConvId];
      const errorPipeline = errorConv.pipeline || {};
      const isActiveConvError = errorConvId === state.activeConversationId;
      
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [errorConvId]: {
            ...errorConv,
            pipeline: {
              ...errorPipeline,
              pipelineActive: false,
              pipelineComplete: true,
              pipelineError: action.payload.error
            }
          }
        },
        ...(isActiveConvError ? {
          pipelineActive: false,
          pipelineComplete: true,
          pipelineError: action.payload.error
        } : {})
      };
    }

    // ========================================
    // RESET PROGRESS
    // ========================================
    case RESET_PROGRESS: {
      const resetConvId = action.payload?.conversationId || state.activeConversationId;
      
      if (!resetConvId) {
        return {
          ...state,
          pipelineActive: false,
          pipelineComplete: false,
          pipelineError: null,
          stages: [],
          currentStage: null,
          agentMessages: [],
          executionPath: null,
        };
      }
      
      const resetConv = state.conversations[resetConvId];
      if (!resetConv) {
        return state;
      }
      
      const isActiveConv = resetConvId === state.activeConversationId;
      
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [resetConvId]: {
            ...resetConv,
            pipeline: {
              pipelineActive: false,
              pipelineComplete: false,
              pipelineError: null,
              stages: [],
              currentStage: null,
              agentMessages: [],
              executionPath: null,
            }
          }
        },
        ...(isActiveConv ? {
          pipelineActive: false,
          pipelineComplete: false,
          pipelineError: null,
          stages: [],
          currentStage: null,
          agentMessages: [],
          executionPath: null,
        } : {})
      };
    }

    case UPDATE_MESSAGE_FEEDBACK: {
      const { messageId, feedbackType, feedbackText } = action.payload;
      
      // Update message in the active conversation
      const updatedMessages = state.messages.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            feedback: feedbackType,
            feedbackText: feedbackText || null,
            feedbackTimestamp: new Date().toISOString(),
          };
        }
        return msg;
      });
      
      return {
        ...state,
        messages: updatedMessages,
      };
    }

    // ========================================
    // REMOVE AGENT MESSAGE (for regeneration)
    // ========================================
    case REMOVE_AGENT_MESSAGE: {
      const { messageId } = action.payload;
      const activeConvId = state.activeConversationId;
      
      if (!activeConvId) return state;
      
      const conversationMessages = state.conversations[activeConvId]?.messages || [];
      
      const updatedMessages = conversationMessages.filter(msg => msg.id !== messageId);
      
      const updatedConversations = {
        ...state.conversations,
        [activeConvId]: {
          ...state.conversations[activeConvId],
          messages: updatedMessages
        }
      };
      
      return {
        ...state,
        conversations: updatedConversations,
        messages: updatedMessages,
      };
    }

    // ========================================
    // REGENERATE MESSAGE
    // ========================================
    case REGENERATE_MESSAGE_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case REGENERATE_MESSAGE_SUCCESS: {
      const { response, conversationId: responseConvId } = action.payload;
      const targetConvId = responseConvId || state.activeConversationId;
      
      if (!targetConvId) {
        return state;
      }
      const messageWithCorrectId = {
        ...response,
        id: response.metadata?.original_message_id || response.id 
      };
      
      const updatedConversationsRegen = {
        ...state.conversations,
        [targetConvId]: {
          ...state.conversations[targetConvId],
          messages: [
            ...(state.conversations[targetConvId]?.messages || []),
            messageWithCorrectId
          ]
        }
      };
      
      return {
        ...state,
        conversations: updatedConversationsRegen,
        messages: updatedConversationsRegen[state.activeConversationId]?.messages || [],
        isLoading: false,
        error: null,
      };
    }

    case REGENERATE_MESSAGE_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
      };

    // ========================================
    // CATEGORY SELECTION SUBMITTED
    // ========================================
    case CATEGORY_SELECTION_SUBMITTED: {
      const { messageId: selMsgId, selectedValues: selValues } = action.payload;
      const activeConvId = state.activeConversationId;
      if (!activeConvId) return state;

      const updatedMessages = (state.conversations[activeConvId]?.messages || []).map(msg =>
        msg.id === selMsgId
          ? { ...msg, submitted: true, selectedValues: selValues }
          : msg
      );

      return {
        ...state,
        conversations: {
          ...state.conversations,
          [activeConvId]: {
            ...state.conversations[activeConvId],
            messages: updatedMessages,
          },
        },
        messages: updatedMessages,
      };
    }

    // ========================================
    // STORE CATEGORY SELECTION
    // ========================================
    case STORE_CATEGORY_SELECTION_REQUEST:
      return {
        ...state,
        // Could add loading state if needed
      };

    case STORE_CATEGORY_SELECTION_SUCCESS:
      return {
        ...state,
        // Category selection message has been stored successfully
      };

    case STORE_CATEGORY_SELECTION_FAILURE:
      return {
        ...state,
        error: action.payload.error,
      };

    default:
      return state;
  }
};

export default chatAgentReducer;
